// The imperative heart of the route transition. A single `progress` number (0 → 1, where 0 = origin
// visible, 1 = destination visible) is the source of truth; the finger-drag and the release/auto spring do
// nothing but mutate it and re-apply transforms. That's what makes it attached-to-the-gesture AND
// interruptible.
//
// In BOTH modes the incoming pane is a LIVE, loading route — never a frozen picture — sliding over a
// SNAPSHOT of the outgoing view. What supplies it differs, because the router only ever renders one <Outlet/>:
//   • GESTURE (begin/setProgress/release): the finger drag. The URL stays on the origin until the drag
//     completes, so the live <Outlet/> is still the origin and the incoming pane is a SHADOW ROUTER pinned to
//     the destination (route-preview.tsx) — it runs the real loaders, so holding a half-finished swipe lets
//     the destination finish loading under your finger. A cancelled drag is a pure no-op (nothing committed).
//   • PROGRAMMATIC (animate/interruptNav): a Link click or back/forward button, already committed by the
//     router. Auto-springs toward the destination; the incoming pane is the same live preview a drag uses, so
//     a rapid opposite navigation is just the spring re-aiming to 0 (see interruptNav) — both panes stay put.
//
// Layering by edge (progress 0 → 1): edge 'right' = forward (incoming enters from the right ON TOP, outgoing
// trails left); edge 'left' = backward (outgoing slides off right ON TOP, incoming revealed from the left).
// What those two directions are CALLED depends on the surface's kind — next/previous when LATERAL, push/pop
// when HIERARCHICAL (see surface.config.ts) — but the geometry below is the same either way. At
// progress 0 the origin pane covers the viewport; at 1 the destination does — so the live layer's route swap
// (commit / revert) stays hidden behind an opaque pane, seam-free.
//
// The active-link highlight is decoupled from the URL: `apply` pushes origin/destination into navVisualPath
// as progress crosses 0.5, so the header flips at the midpoint (see nav-visual-path.ts).

import { TRANSITION_CONFIG } from '../core/motion.config'
import {
  incomingInteractive,
  matchesFrom,
  type RouteTransition,
  type SurfaceKind,
  type TransitionPhase,
} from './surface.config'
import { setFlag } from '../core/dom-flag'
import { captureSnapshot } from './snapshot'
import { navVisualPath, transitionProgress } from '../core/nav-visual-path'
import { rtlog, contentTag } from '../core/debug'
// Spring maths live in their own module (spring.ts) so the scroll-snap engine can reuse them without importing
// this whole gesture controller — the decoupling that lets the two engines evolve independently.
import { SPRING_HZ, springSamples, springProgressAt } from '../core/spring'
import { createMidpointHaptic } from '../core/haptics'

/** The phase union is part of the shared contract now (`incomingInteractive` reads it) — see surface.config. */
type State = TransitionPhase
type Mode = 'gesture' | 'programmatic'
/**
 * A transition pane. `managed` marks a pane REACT owns and has already placed in the root (the live
 * destination preview of a drag — see route-preview.tsx): the controller styles and transforms it like any
 * other, but must never append or remove it, only hand it back on teardown.
 */
type Snapshot = { layer: HTMLElement; applyScroll: () => void; managed?: boolean }

interface ControllerCallbacks {
  /** Commit navigation to `to` — called when a gesture COMPLETES. May return a promise settling on resolve. */
  navigate: (to: string) => unknown
  /** The router's CURRENT committed pathname. Read at commit time (see `finish`) to detect a foreign
   *  navigation that landed mid-settle — our deferred commit must never stomp the user's explicit click. */
  getLocation: () => string
  /** Unmount the React-managed live preview pane, if one was armed. Called on teardown. */
  releasePreview?: () => void
}

const { parallax, commitThreshold, flickVelocity, intentSlop, spring, shadow } = TRANSITION_CONFIG
const APP_BG = 'var(--background)'

/**
 * How present the leading-edge shadow is at this progress: a half-sine, so it's zero at BOTH endpoints and
 * fullest mid-travel. The shadow therefore eases in as the top pane lifts away from its edge and eases back
 * out as it lands. Zero at the ends is the point — at rest the pane is flush against the edge with nothing to
 * cast onto, and a shadow that's simply switched on when the transition arms and stripped at teardown is
 * visible doing both.
 */
function shadowFade(progress: number): number {
  const p = progress < 0 ? 0 : progress > 1 ? 1 : progress
  return Math.sin(Math.PI * p)
}
/**
 * Last-resort backstop for a drag, NOT a limit on how long you may hold one. Resting mid-swipe is a first-
 * class thing to do — you hold the pane still and watch the destination finish loading in it — so this has to
 * sit far beyond any human pause. A lost pointer release is caught by real signals instead (window
 * pointerup/pointercancel, a move with no buttons down, blur, and the stale-gesture check on the next press);
 * this only catches the pathological case where a wedged drag is left covering the screen with no input at all.
 */
const DRAG_WATCHDOG_MS = 60_000
/**
 * Bounds on a drag glide segment's duration (see `glideToward`). A segment nominally lasts one input
 * interval — the time until the next pointer sample replaces it — measured from the actual event cadence.
 * The floor absorbs timestamp jitter on 120Hz-input devices (where a segment this short is equivalent to the
 * old direct write anyway); the ceiling keeps the pane from drifting in slow motion toward a sample that
 * arrived after a long stall (a finger resting, a dropped frame) — past ~2 frames it should just go there.
 */
const DRAG_SEGMENT_MIN_MS = 8
const DRAG_SEGMENT_MAX_MS = 34
/** Segment duration before any cadence has been measured (one 60Hz input interval). */
const DRAG_SEGMENT_DEFAULT_MS = 17

export class TransitionController {
  private state: State = 'idle'
  private mode: Mode = 'gesture'
  private progress = 0
  private width = 0
  private transition: RouteTransition | null = null
  private originPath = ''
  private destPath = ''
  /** This surface's midpoint tick (see haptics.ts), fed from publish(). */
  private readonly midHaptic = createMidpointHaptic()

  private topEl: HTMLElement | null = null
  private bottomEl: HTMLElement | null = null
  /** Snapshot layers to REMOVE on teardown. The live layer (always the incoming pane) is reset, never removed. */
  private snapshotEls: HTMLElement[] = []
  /** The React-owned live preview pane, if this transition armed one. Reset on teardown, never removed. */
  private managedEl: HTMLElement | null = null
  /** Gradient strip riding just off the top pane's leading edge — the "shadow" it casts on the pane beneath. */
  private shadowEl: HTMLElement | null = null
  /** Whichever pane is showing the DESTINATION. Becomes the outgoing pane when a gesture chains onward. */
  private incomingEl: HTMLElement | null = null
  /** True once dispose() has run, so a teardown during unmount doesn't call back into a dead React tree. */
  private disposed = false
  /** The route the live <Outlet/> currently holds, reported by the outlet on every commit. See setLivePath. */
  private livePath = ''
  /** While finishing: the route the live layer must reach before the covering panes may be dropped. */
  private awaiting: string | null = null
  private topX: (p: number) => number = () => 0
  private bottomX: (p: number) => number = () => 0

  private raf = 0
  private lastFrame = 0
  private springV = 0
  private springTarget: 0 | 1 = 0
  /** The composited settle: one Animation per pane, plus the precomputed trajectory they were built from. */
  private animations: Animation[] = []
  private samples: number[] = []
  /** The composited DRAG: the current glide segment's animations, the progress span they travel, and the
   *  document-timeline anchor (`startT`) every one of them was explicitly started at. */
  private dragAnims: Animation[] = []
  private dragSeg: { from: number; to: number; dur: number; startT: number } | null = null
  /** When the last pointer sample arrived, so a segment's duration can match the real input cadence. */
  private lastDragSampleT = 0
  /** Backstop timer: a settling/finishing phase must never outlive this, or a stuck promise / dropped rAF
   *  would leave a snapshot covering the viewport forever (navigations then look "frozen"). */
  private watchdog = 0
  /** >0 while a navigation this controller issued (gesture commit / cancel revert) is in flight. See isInternalNav. */
  private internalNav = 0
  /**
   * Whether the ROUTER currently sits on the destination (true) or is still on the origin (false). A gesture
   * starts false (it only commits if you complete it); a link/back-forward slide starts true (the router
   * already moved). `finish` reads this to work out what — if anything — it must navigate, which is what lets
   * an in-flight transition be grabbed and dragged either way from ANY state.
   */
  private committed = false
  /**
   * Whether a finger has actually MOVED the panes since it took hold of them — the second input to the
   * interactivity gate (see `incomingInteractive`). A fresh drag is moved by construction (it only arms once
   * horizontal intent is decisive); a CATCH starts unmoved and stays that way until the first pointer sample
   * that shifts progress, which is what lets a tap on a caught transition reach the page it landed on.
   */
  private movedByFinger = false
  /** Progress at the instant the current finger took hold — the origin `movedByFinger`'s slop is measured from. */
  private grabProgress = 0

  constructor(
    private readonly root: HTMLElement,
    private readonly live: HTMLElement,
    private readonly callbacks: ControllerCallbacks,
    /** Which KIND of route transition this surface performs (see SurfaceKind). LATERAL — peer pages, a tab
     *  strip: no depth cues, both panes travel at full speed (parallax factor 1) and the seam is a hairline
     *  rather than a cast shadow. HIERARCHICAL — the root push/pop: the under-pane trails and the leading
     *  edge casts a shadow. */
    private readonly kind: SurfaceKind,
    /** Whether this surface lets the arriving page be touched mid-transition — see `interactiveIncoming` on
     *  TransitionOutletProps for the policy and `incomingInteractive` for the exact rule. */
    private readonly interactiveIncoming = false,
  ) {}

  /**
   * The ONE place `state` is written, so the pane flags can never desync from the phase. Every transition of the
   * machine funnels through here; `applyPaneFlags` then re-derives which panes may be touched (guarded writes,
   * so re-deriving the same answer costs nothing and — crucially for `inert` — doesn't move focus).
   */
  private setState(next: State): void {
    this.state = next
    this.applyPaneFlags()
  }

  /**
   * Who is reachable — by pointer, by keyboard and to a screen reader — while a transition is on screen.
   *
   * A transition puts TWO renders of the app in the document at once, and by default neither is reachable: the
   * outgoing pane is `pointer-events: none` (see snapshot.ts) and the incoming one is `inert`. That is why
   * duplicate focusables were never a problem to solve. Un-inerting the incoming pane makes it one: without
   * this, a Tab from the arriving page would walk into the DEAD CLONE of the page being left, and a screen
   * reader would read both. So the rule is symmetric — exactly one pane is live at a time:
   *
   *   • the incoming pane  — `inert` unless `incomingInteractive` says a finger isn't moving it;
   *   • the outgoing clone — always `inert`: it is a picture, and `pointer-events: none` alone leaves it in the
   *     tab order and the a11y tree;
   *   • the live <Outlet/>  — `inert` too while an incoming pane covers it (it is the origin, behind two panes),
   *     but NEVER when it holds the focused element: inerting the subtree the user is typing in would blur it,
   *     and a cancelled gesture is supposed to be a pure no-op.
   */
  private applyPaneFlags(): void {
    const live = incomingInteractive(this.interactiveIncoming, this.state, this.movedByFinger)
    if (this.managedEl) setFlag(this.managedEl, 'inert', !live)
    if (!this.interactiveIncoming) return
    // Every tracked non-managed layer is a clone (the outgoing pane, and the incoming one on the no-live-preview
    // fallback path) — pictures, all of them.
    for (const el of this.snapshotEls) setFlag(el, 'inert', true)
    const holdsFocus = this.live.contains(document.activeElement)
    setFlag(this.live, 'inert', this.state !== 'idle' && this.managedEl !== null && !holdsFocus)
  }

  /** The kind, as the one question the geometry actually asks of it: are the two pages PEERS? Everything the
   *  kind changes (the under-pane's trail, the seam being a shadow or a hairline) reads this rather than
   *  comparing the string, so adding a third kind can't silently take the hierarchical branch. */
  private get lateral(): boolean {
    return this.kind === 'lateral'
  }

  getState(): State {
    return this.state
  }

  getProgress(): number {
    return this.progress
  }

  getActiveTransition(): RouteTransition | null {
    return this.transition
  }

  /**
   * Is a finger currently MOVING the panes (as opposed to resting on them)? The pointer layer reads it to decide
   * whether the release must swallow a click, so that decision and the interactivity gate's share one slop
   * threshold instead of each judging "did it move" for itself.
   */
  isFingerMoving(): boolean {
    return this.state === 'dragging' && this.movedByFinger
  }

  /** True when a programmatic slide is mid-flight and a new navigation can re-aim/reverse it. */
  canRetarget(): boolean {
    return this.mode === 'programmatic' && this.state === 'settling'
  }

  /**
   * True whenever a transition is in flight and can be CAUGHT — the iOS behaviour: put a finger down on a
   * moving transition and it stops dead under you, hold it anywhere, drag it either way, let go and it
   * resolves from there. It doesn't matter whether the slide came from a drag, a Link click or the back
   * button, nor whether the URL has committed yet: `finish` reconciles the router with wherever you drop it.
   */
  canRegrab(): boolean {
    return this.state === 'settling' || this.state === 'finishing'
  }

  /**
   * True while a navigation WE issued is in flight (the gesture's commit at `begin`, or the revert a cancelled
   * gesture performs). The outlet's link handler must ignore these: they are already being animated by this
   * controller, and treating the cancel-revert as a fresh link nav force-resets mid-cancel, tearing the
   * covering snapshot away and flashing the destination before it snaps back.
   */
  isInternalNav(): boolean {
    return this.internalNav > 0
  }

  /** Force back to a clean idle state, revealing the live layer. Safe to call anytime — used to recover if a
   *  navigation lands while a previous transition is wedged (e.g. a superseded gesture-cancel revert). */
  forceReset(): void {
    this.mark('forceReset')
    this.cancelAnimations()
    if (this.raf) {
      cancelAnimationFrame(this.raf)
      this.raf = 0
    }
    if (this.state !== 'idle') this.teardown()
  }

  /**
   * A navigation landed while a link slide is still running. Because BOTH panes are stable — a snapshot of
   * the origin and a live preview of the destination, neither of which is the live <Outlet/> — an interruption
   * is almost always just the spring changing its mind:
   *   • heading to the same place again → keep going (re-aim to 1)
   *   • heading back where we came from → reverse (re-aim to 0); the origin's pane is already sitting there
   *   • a third route entirely → re-arm from scratch
   * (The previous design used the live layer as the incoming pane, so a reverse had to swap a freshly-frozen
   * snapshot into its slot — and bred a whole class of which-pane-is-which bugs. Panes that never change
   * roles are what make reverse a pure re-aim.)
   */
  interruptNav(
    next: RouteTransition,
    originPath: string,
    makeDeparting: () => Snapshot,
    makeIncoming: () => Snapshot | null,
  ): void {
    if (!this.canRetarget() || !this.transition) return
    const running = this.transition
    if (next.to === running.to) {
      this.mark('interrupt→retarget', { to: next.to })
      this.committed = true
      this.retarget(1)
    } else if (next.to === running.from) {
      this.mark('interrupt→reverse', { to: next.to })
      this.committed = false // the router is heading back to where this slide started
      this.retarget(0)
    } else {
      // `makeDeparting` is a factory for the same reason `makeIncoming` is: it deep-clones the live page, and
      // only this branch needs it. The retarget/reverse paths above are the RAPID back/forward moments where a
      // dropped frame is most visible — they must not pay a full-page clone just to throw it away.
      const departing = makeDeparting()
      this.mark('interrupt→redirect', { to: next.to, departing: contentTag(departing.layer) })
      this.redirect(next, originPath, departing, makeIncoming)
    }
  }

  /**
   * The route the live <Outlet/> is currently showing. The outlet reports it on every commit.
   *
   * Teardown drops the panes and reveals that layer, so it is only safe once the layer actually holds the route
   * the panes landed on — otherwise the slide would end by revealing the page you just left. Waiting on this,
   * rather than on the navigation promise, is what lets the animation start BEFORE the router has rendered
   * anything.
   */
  setLivePath(path: string): void {
    this.livePath = path
    if (this.state !== 'finishing' || !this.awaiting) return
    if (!matchesFrom(path, this.awaiting)) {
      // A route we are NOT waiting for has already rendered underneath. Only our own commit is worth waiting
      // out — while the router is still bound for `awaiting` (its location moves at the start of a navigation),
      // an intermediate render means nothing and the pane holds. But if the router has gone somewhere else, the
      // covering pane is now hiding a page the user asked for, and holding it until the watchdog is a second of
      // frozen screen. `interactiveIncoming` is what made this reachable by tapping — a link on the arriving
      // pane, tapped in the window between the commit and the reveal — but the hole was always there for any
      // navigation that lands in it (keyboard, programmatic), so the fix isn't gated on the flag.
      if (matchesFrom(this.callbacks.getLocation(), this.awaiting)) return
      this.mark('await-live→superseded', { path, awaiting: this.awaiting })
      this.awaiting = null
      this.teardown()
      return
    }
    this.awaiting = null
    this.mark('live-committed', { path })
    // Two frames: one for React to paint the committed route, one for the compositor to show it. Then the
    // covering pane can go without a flash.
    requestAnimationFrame(() => requestAnimationFrame(() => this.teardown()))
  }

  // ── Gesture ───────────────────────────────────────────────────────────────

  /**
   * Begin a finger drag. The URL does NOT change here — the destination is only committed if the gesture is
   * completed (see `finish`). Two reasons: the drag stays perfectly smooth (no loaders/re-render churning
   * under the finger, which is what made the start feel sluggish), and a cancelled drag is a pure no-op — no
   * revert, so nothing can flash. The destination's chunk + data are preloaded on pointer-down (see the
   * outlet), so the commit at the end paints immediately.
   *
   * The live <Outlet/> layer sits underneath, still showing the origin, so the OUTGOING pane is a snapshot of
   * it and the INCOMING pane is supplied by the caller — a live shadow render of the destination, so it can
   * finish loading mid-drag (see route-preview.tsx).
   *
   * `makeIncoming` is a factory, not a pane: it mounts React state, so it must not run unless this call
   * actually arms a transition — otherwise a no-op `begin` would strand a preview pane over the live page.
   */
  begin(transition: RouteTransition, originPath: string, makeIncoming: () => Snapshot | null): boolean {
    if (this.state !== 'idle') return false
    this.reconcile() // clear any orphan a prior wedged transition left, so this one starts from a clean stage
    const incoming = makeIncoming()
    if (!incoming) return false // no live pane for the destination → no slide (see the outlet's incomingFor)
    this.mode = 'gesture'
    this.committed = false // the URL stays on the origin unless this gesture is completed
    // A fresh drag is finger-moved by construction: `begin` is only reached once horizontal intent is decisive,
    // so the pane is already travelling. (A CATCH is the case that starts unmoved — see interrupt.)
    this.movedByFinger = true
    this.arm(transition, originPath, captureSnapshot(this.live), incoming)
    this.setState('dragging')
    this.progress = 0
    this.apply()
    this.mark('begin', { from: originPath, to: transition.to })
    this.armWatchdog(DRAG_WATCHDOG_MS)
    return true
  }

  /** Run a navigation WE own, flagged so the outlet's link handler ignores it (see isInternalNav). */
  private runInternalNav(run: () => unknown): Promise<unknown> {
    this.internalNav++
    return Promise.resolve(run()).finally(() => {
      // Release strictly AFTER onBeforeLoad/onResolved for this navigation (they fire around the promise, and
      // the flag must still be up for them). A timeout, not a rAF: rAF never fires in a hidden tab, and a
      // commit finishing while the user was on another tab would leave the flag stuck up for the session.
      setTimeout(() => {
        this.internalNav = Math.max(0, this.internalNav - 1)
      }, 120)
    })
  }

  setProgress(p: number): void {
    if (this.state !== 'dragging') return
    const target = p < 0 ? 0 : p > 1 ? 1 : p
    // The moment a caught transition actually MOVES, the finger owns the panes and the incoming one stops being
    // touchable (see incomingInteractive). Measured from where the catch took hold and against the SAME slop a
    // fresh gesture uses to claim the pointer, in the same pixels: a tap's tremor is a couple of px and must not
    // count as a drag, or catching a settle to tap it would go dead on the shakiest thumbs.
    if (!this.movedByFinger && Math.abs(target - this.grabProgress) * this.width >= intentSlop) {
      this.movedByFinger = true
      this.applyPaneFlags()
    }
    if (this.canComposite()) {
      // Glide BEFORE moving `progress`: with no segment running yet (the first move of a drag/re-grab),
      // dragVisualProgress falls back to `progress`, which at this point still holds the position the panes
      // were last applied at — the correct start for the first segment.
      this.glideToward(target)
      this.progress = target
      this.publish()
    } else {
      this.progress = target
      this.apply()
    }
    this.armWatchdog(DRAG_WATCHDOG_MS) // each move pushes the backstop out
  }

  /**
   * Move the panes to the finger via the COMPOSITOR, not a style write. A style write can never exceed the
   * main thread's update rate, and on a ProMotion iPhone Safari holds pointer delivery and main-thread
   * rendering to 60Hz while the panel runs 120Hz (only compositor animations get the full rate) — so a drag
   * applied per event is a 60Hz sample-and-hold staircase next to a settle that already runs at 120Hz.
   * Instead, each pointer sample REPLACES a short linear segment: from wherever the panes visually are to the
   * new finger position, lasting one input interval, so the compositor renders the in-between frames at the
   * panel's rate. Cost: the pane trails the finger by at most one input frame (~17ms) — motion-blurred under
   * a flick, sub-pixel under the slow drag where the staircase was most visible.
   */
  private glideToward(target: number): void {
    const now = this.glideClock()
    // The next segment starts from where the panes have VISUALLY got to — reconstructed from the running
    // segment's anchor on this same clock, NEVER from the Animation's own currentTime: a just-issued segment
    // is play-pending and the browser assigns its start time at a later rendering update, so that readback
    // was nondeterministically 0–2 events stale and each replacement segment yanked the panes back before
    // re-travelling (visible jitter). Self-anchored start times make consecutive segments join exactly.
    const from = this.dragVisualProgress()
    const elapsed = now - this.lastDragSampleT
    this.lastDragSampleT = now
    const dur =
      elapsed > 0 && elapsed <= DRAG_SEGMENT_MAX_MS * 2
        ? Math.min(DRAG_SEGMENT_MAX_MS, Math.max(DRAG_SEGMENT_MIN_MS, elapsed))
        : DRAG_SEGMENT_DEFAULT_MS
    for (const a of this.dragAnims) a.cancel()
    this.dragAnims = []
    // `fill: forwards` holds the pane at the finger when the segment outlives the input (a finger at rest
    // sends no moves). Cancel-then-animate in one task is seam-free: style changes only render between tasks,
    // so no frame can paint the gap.
    const options: KeyframeAnimationOptions = { duration: dur, easing: 'linear', fill: 'forwards' }
    const glide = (el: HTMLElement | null, map: (p: number) => number) => {
      if (!el) return
      this.dragAnims.push(
        el.animate(
          [{ transform: `translate3d(${map(from)}px,0,0)` }, { transform: `translate3d(${map(target)}px,0,0)` }],
          options,
        ),
      )
    }
    glide(this.topEl, this.topX)
    glide(this.bottomEl, this.bottomX)
    if (this.shadowEl) {
      this.dragAnims.push(
        this.shadowEl.animate(
          [
            {
              transform: `translate3d(${this.topX(from) - this.stripWidth()}px,0,0)`,
              opacity: this.stripOpacity(from).toFixed(3),
            },
            {
              transform: `translate3d(${this.topX(target) - this.stripWidth()}px,0,0)`,
              opacity: this.stripOpacity(target).toFixed(3),
            },
          ],
          options,
        ),
      )
    }
    // Anchor every segment to the SHARED clock, explicitly. Assigning `startTime` skips the play-pending
    // phase — the one nondeterminism in this whole scheme — so all three panes start in lockstep at a time WE
    // recorded, and the reconstruction below is exact rather than "whenever the browser got around to it".
    for (const a of this.dragAnims) a.startTime = now
    this.dragSeg = { from, to: target, dur, startT: now }
  }

  /** The clock glide segments are anchored to: the document timeline (what animations actually run on),
   *  falling back to performance.now() — the same time origin — before the timeline's first tick. */
  private glideClock(): number {
    const t = document.timeline?.currentTime
    return typeof t === 'number' ? t : performance.now()
  }

  /** Where the composited drag has visually got to — the running segment sampled at its anchored clock. With
   *  no glide running it falls back to `progress` — the position the panes were last APPLIED at (arm/re-grab/
   *  fallback all write styles at exactly that value, and setProgress only advances it after issuing a glide). */
  private dragVisualProgress(): number {
    const seg = this.dragSeg
    if (!seg) return this.progress
    const t = this.glideClock() - seg.startT
    const k = seg.dur <= 0 ? 1 : t < 0 ? 0 : t > seg.dur ? 1 : t / seg.dur
    return seg.from + (seg.to - seg.from) * k
  }

  private cancelDragAnims(): void {
    for (const a of this.dragAnims) a.cancel()
    this.dragAnims = []
    this.dragSeg = null
  }

  release(velocityX: number): void {
    if (this.state !== 'dragging' || !this.transition) return
    const sign = this.transition.edge === 'right' ? -1 : 1
    const towardCommit = sign * velocityX
    let target: 0 | 1
    if (towardCommit > flickVelocity) target = 1
    else if (towardCommit < -flickVelocity) target = 0
    else target = this.progress > commitThreshold ? 1 : 0
    // Decide from the FINGER (above), but spring from where the panes VISUALLY are: the drag glide trails the
    // finger by up to one input frame, and a spring seeded at the finger would open the release with a jump.
    this.progress = this.dragVisualProgress()
    // The finger is off: nothing is moving the panes by hand any more, so the arriving page becomes touchable for
    // the whole settle (`startSpring` → setState re-derives the flags).
    this.movedByFinger = false
    // Settle the URL the INSTANT the finger lifts, not when the spring lands. A completed gesture commits its
    // destination NOW, so the address bar is correct immediately and a follow-up swipe chains from the new page
    // instead of repeating this one from the stale origin the router sat on until the ~300ms settle finished.
    // The spring that follows is purely cosmetic: the incoming preview pane already shows the destination and
    // keeps covering, so the router catching up underneath is invisible — and the settle is composited, so its
    // render can't stutter the panes. `finish()` then sees `committed` already true and only drops the panes
    // once the live layer holds the route. A release BELOW the threshold (target 0) stays a pure no-op.
    if (target === 1 && !this.committed) {
      const to = this.transition.to
      this.committed = true
      this.mark('release→commit', { to })
      void this.runInternalNav(() => this.callbacks.navigate(to))
    }
    this.startSpring(target, (sign * velocityX * 1000) / this.width)
  }

  /**
   * KEEP FLIPPING. Land the in-flight transition right now and immediately arm the next one, anchored exactly
   * where this one ended — so a finger that keeps pushing past the destination carries straight on to the page
   * after it, instead of stalling against the end of a settle it has to sit and wait out.
   *
   * The trick is what becomes the new OUTGOING pane: a snapshot of the pane currently showing the destination.
   * At the moment of the chain that pane covers the viewport, and the new transition starts at progress 0 with
   * it covering the viewport — the same pixels in the same place, so the swap is invisible and the finger never
   * feels a seam. Meanwhile the router commits to that destination in the background, hidden behind the panes.
   *
   * Returns false if there's nothing to chain from (idle, or no pane to hand over).
   */
  chain(next: RouteTransition, makeIncoming: () => Snapshot | null): boolean {
    if (this.state === 'idle' || !this.transition || !this.incomingEl) return false
    const landedOn = this.destPath
    // Freeze the destination exactly as it looks right now. Strip the inline styles WE put on that pane — its
    // transform above all — or the clone would carry the old transition's offset into the new one.
    this.cancelAnimations() // take the panes back before we snapshot one of them and re-arm
    const landed = captureSnapshot(this.incomingEl)
    const clone = landed.layer.firstElementChild
    if (clone instanceof HTMLElement) {
      clone.style.transform = ''
      clone.style.zIndex = ''
      clone.style.boxShadow = ''
      clone.style.position = 'static'
    }

    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    for (const el of this.snapshotEls) el.remove()
    this.snapshotEls = []
    this.releaseManaged()
    this.resetLive()

    this.mark('chain', { from: landedOn, to: next.to, committed: this.committed })
    // The gesture we're chaining OFF completed, so its destination is where the router belongs. (A link slide
    // already committed it — nothing to do.) This runs behind the covering pane, so it is never seen.
    if (!this.committed) void this.runInternalNav(() => this.callbacks.navigate(landedOn))

    const incoming = makeIncoming()
    if (!incoming) {
      this.teardown()
      return false
    }
    this.mode = 'gesture'
    this.committed = false // the new destination is only committed if THIS gesture completes too
    this.movedByFinger = true // a chain only happens mid-drag, past the end of the transition it hands over from
    this.arm(next, landedOn, landed, incoming)
    this.setState('dragging')
    this.progress = 0
    this.apply()
    this.armWatchdog(DRAG_WATCHDOG_MS)
    return true
  }

  /** Catch an in-flight transition (settling, or even mid-commit) and hand control back to the finger. */
  interrupt(): void {
    if (!this.canRegrab()) return
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    // Catch the pane exactly where the compositor had got it to, THEN take it back. Reading the animation's own
    // clock (rather than the element's computed transform) keeps one source of truth and costs no layout.
    this.progress = this.settleProgress()
    this.cancelAnimations()
    // A catch starts UNMOVED, and that is the whole point: this press may still be a tap on the page that is
    // landing, so the pane it lands on stays touchable until the finger actually drags it (see setProgress).
    this.movedByFinger = false
    this.grabProgress = this.progress
    this.setState('dragging')
    this.apply()
    this.armWatchdog(DRAG_WATCHDOG_MS)
    this.mark('interrupt', { at: Math.round(this.progress * 100) / 100 })
  }

  // ── Programmatic ──────────────────────────────────────────────────────────

  /**
   * Play a Link / back-forward slide — WITHOUT waiting for the router to render anything. The incoming pane
   * is a live PREVIEW of the destination (the same shadow router a drag uses), so the slide starts on the
   * very frame the link is clicked; the real router commits underneath, hidden behind the panes, and
   * `setLivePath` releases the teardown once it has. (Animating the live layer instead — the old design —
   * put the destination's render on the click's critical path: up to 150ms of nothing moving at 6× throttle.)
   * The standing cost is the destination rendering twice, preview + real; it buys the page moving the
   * instant you touch it.
   */
  animate(transition: RouteTransition, originPath: string, outgoing: Snapshot, incoming: Snapshot): void {
    if (this.state !== 'idle') return
    this.reconcile() // clear any orphan a prior wedged transition left, so this one starts from a clean stage
    this.mode = 'programmatic'
    this.committed = true // the router is already on its way (a Link click / back-forward); we just animate it
    this.arm(transition, originPath, outgoing, incoming)
    this.progress = 0
    this.apply()
    this.mark('animate', { from: originPath, to: transition.to, outgoing: contentTag(outgoing.layer) })
    this.startSpring(1, 0)
  }

  /** Re-arm the slide from scratch toward `next`, freezing the current live route as the outgoing pane. Used
   *  for interrupts that aren't a clean single reverse (forward-after-reverse, a third route) — correctness
   *  over a jump-free reverse: the leaving page covers, then the new destination slides in. */
  private redirect(
    next: RouteTransition,
    originPath: string,
    departing: Snapshot,
    makeIncoming: () => Snapshot | null,
  ): void {
    this.cancelAnimations()
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    for (const el of this.snapshotEls) el.remove()
    this.snapshotEls = []
    this.releaseManaged()
    this.live.style.transform = ''
    this.live.style.zIndex = ''
    this.live.style.boxShadow = ''
    const incoming = makeIncoming()
    if (!incoming) {
      this.teardown()
      return
    }
    this.committed = true
    this.arm(next, originPath, departing, incoming)
    this.progress = 0
    this.apply()
    this.startSpring(1, 0)
  }

  /** Re-aim a running programmatic spring (0 = origin, 1 = destination) — used to resume forward after a
   *  reverse. The already-covering snapshot matches the committed route, so no element swap is needed. */
  retarget(target: 0 | 1): void {
    if (this.mode !== 'programmatic' || this.state !== 'settling') return
    // Re-aim from wherever the settle actually is, carrying its speed, so the pane changes its mind without
    // ever jumping. (A composited settle can't just have its target mutated — it's already been handed over —
    // so it is re-integrated and re-handed from the current point.)
    this.progress = this.settleProgress()
    this.startSpring(target, this.settleVelocity())
  }

  /** The settle's current speed (progress units / second), for re-aiming without losing momentum. */
  private settleVelocity(): number {
    if (this.samples.length < 2) return 0
    const lead = this.animations[0]
    const elapsed = lead ? Number(lead.currentTime ?? 0) : 0
    const index = Math.round((elapsed / 1000) * SPRING_HZ)
    const here = this.samples[Math.min(index, this.samples.length - 1)]
    const next = this.samples[Math.min(index + 1, this.samples.length - 1)]
    if (here === undefined || next === undefined) return 0
    return (next - here) * SPRING_HZ
  }

  // ── Shared internals ──────────────────────────────────────────────────────

  /**
   * Arm the panes for a fresh transition. `incoming` defaults to the LIVE layer (the programmatic path: the
   * router already committed, so the live tree IS the destination and streams in as it slides). The GESTURE
   * path passes an explicit incoming snapshot instead, because it hasn't navigated yet — see `begin`.
   */
  private arm(transition: RouteTransition, originPath: string, outgoing: Snapshot, incoming?: Snapshot): void {
    this.transition = transition
    this.originPath = originPath
    this.destPath = transition.to
    this.width = this.root.clientWidth || 1
    const incomingEl = incoming ? incoming.layer : this.live
    const W = this.width
    // A LATERAL (peer) surface has no "underneath": both panes travel the full distance side by side.
    const trail = this.lateral ? 1 : parallax
    if (transition.edge === 'right') {
      this.topEl = incomingEl
      this.bottomEl = outgoing.layer
      this.topX = (p) => (1 - p) * W
      this.bottomX = (p) => -p * trail * W
    } else {
      this.topEl = outgoing.layer
      this.bottomEl = incomingEl
      this.topX = (p) => p * W
      this.bottomX = (p) => -(1 - p) * trail * W
    }
    // A `managed` incoming pane is React's: it is already mounted in the root, and unmounting it is React's
    // job on teardown. Everything else we own — append it now, remove it later.
    const adopt = incoming && !incoming.managed
    this.snapshotEls = adopt ? [outgoing.layer, incoming.layer] : [outgoing.layer]
    this.managedEl = incoming?.managed ? incoming.layer : null
    this.incomingEl = incomingEl
    this.live.style.pointerEvents = 'none'
    // With the incoming pane supplied, the live layer is just a backdrop (still showing the origin) — park it
    // beneath them; it is never transformed, so nothing to reset but the z-index.
    if (incoming) this.live.style.zIndex = '0'
    this.root.appendChild(outgoing.layer)
    if (adopt) this.root.appendChild(incoming.layer)
    // Hierarchical surface: the leading-edge SHADOW, and only under a FINGER (a programmatic link slide runs
    // without it — same policy as the scroll engine's data-rt-user gate). LATERAL surface: the hairline
    // BORDER separator instead, in EVERY mode — it divides equals whenever a seam is on screen, links included.
    if (this.lateral || this.mode === 'gesture') this.mountShadow()
    this.styleStack()
    // The panes just changed identity, so re-derive who is reachable — arm() is called from paths that only set
    // the phase afterwards (animate → startSpring), and this keeps it true of the stage the instant it exists.
    this.applyPaneFlags()
    outgoing.applyScroll()
    incoming?.applyScroll()
  }

  /** (Re)apply the opaque backdrop and z-order for the current top/bottom panes. */
  private styleStack(): void {
    this.root.style.overflow = 'hidden'
    this.root.style.background = APP_BG
    for (const el of [this.topEl, this.bottomEl]) {
      if (!el) continue
      el.style.background = APP_BG
      el.style.zIndex = el === this.topEl ? '2' : '1'
      el.style.boxShadow = ''
    }
  }

  /**
   * Mount the strip riding the top pane's leading edge, driven, like the panes, from `progress` (see
   * `apply`). On a hierarchical surface it's the leading-edge SHADOW (a gradient cast onto the pane
   * beneath); on a LATERAL (peer) surface it's a hairline BORDER separating the two equal pages instead. A
   * strip rather than a `box-shadow`/`border` on the pane itself because only `transform` and `opacity`
   * animate on the compositor — re-computing a painted edge every frame repaints a full-screen layer, which
   * is exactly the cost this gesture cannot afford on a phone.
   */
  private mountShadow(): void {
    if (this.shadowEl) return
    const el = document.createElement('div')
    el.setAttribute('data-rt-shadow', '')
    const paint = this.lateral
      ? 'width:1px;background:var(--border);'
      : `width:${shadow.width}px;background:linear-gradient(to left, rgba(0,0,0,${shadow.peakAlpha}), rgba(0,0,0,0));`
    el.style.cssText =
      'position:absolute;top:0;bottom:0;left:0;z-index:2;opacity:0;pointer-events:none;will-change:transform,opacity;' +
      paint
    this.root.appendChild(el)
    this.shadowEl = el
  }

  /** The strip's width — the shadow's gradient band, or the lateral separator's hairline. */
  private stripWidth(): number {
    return this.lateral ? 1 : shadow.width
  }

  /** The strip's presence at `progress`: the shadow eases in/out (see shadowFade); the lateral separator is
   *  simply THERE while a seam is on screen, and gone at the endpoints where the seam meets the stage edge. */
  private stripOpacity(progress: number): number {
    if (!this.lateral) return shadowFade(progress)
    return progress <= 0.001 || progress >= 0.999 ? 0 : 1
  }

  private removeShadow(): void {
    this.shadowEl?.remove()
    this.shadowEl = null
  }

  /**
   * Hand the settle to the compositor. The panes' transforms are driven by Web Animations from precomputed
   * spring keyframes, so a long main-thread task — the destination route's React render, above all — can no
   * longer starve them. What stays on the main thread is only the CHROME clock (`chromeTick`): the midpoint
   * highlight and the tab underline, which read progress back from the animation's own currentTime so there is
   * still exactly one source of truth. If that clock gets starved the underline lags a frame; the page itself
   * keeps gliding, which is the part you actually watch.
   *
   * Falls back to integrating on rAF where `Element.animate` isn't available.
   */
  private startSpring(target: 0 | 1, initialVelocity: number): void {
    this.springTarget = target
    this.springV = initialVelocity
    this.setState('settling')
    this.cancelAnimations()
    this.samples = springSamples(this.progress, target, initialVelocity)
    const durationMs = (this.samples.length / SPRING_HZ) * 1000
    // The backstop must outlive the settle it is guarding, or a long spring (a re-aim from far out) would be
    // torn down while it was still perfectly healthy.
    this.armWatchdog(Math.max(1200, durationMs + 600))
    this.mark('spring', { target, frames: this.samples.length })

    if (!this.canComposite()) {
      this.lastFrame = performance.now()
      this.raf = requestAnimationFrame(this.tick)
      return
    }

    const options: KeyframeAnimationOptions = { duration: durationMs, easing: 'linear', fill: 'forwards' }
    const paneFrames = (map: (p: number) => number) =>
      this.samples.map((p) => ({ transform: `translate3d(${map(p)}px,0,0)` }))

    if (this.topEl) this.animations.push(this.topEl.animate(paneFrames(this.topX), options))
    if (this.bottomEl) this.animations.push(this.bottomEl.animate(paneFrames(this.bottomX), options))
    if (this.shadowEl) {
      this.animations.push(
        this.shadowEl.animate(
          this.samples.map((p) => ({
            transform: `translate3d(${this.topX(p) - this.stripWidth()}px,0,0)`,
            opacity: this.stripOpacity(p).toFixed(3),
          })),
          options,
        ),
      )
    }

    const lead = this.animations[0]
    if (!lead) {
      this.finishSettle(target)
      return
    }
    void lead.finished
      .then(() => {
        if (this.state === 'settling') this.finishSettle(target)
      })
      // A cancel (a re-grab, a retarget, a teardown) rejects `finished` with an AbortError — that IS the normal
      // interruptible path. Anything else is a real bug and must not be swallowed: a blanket catch here hid a
      // ReferenceError thrown inside finish(), and all you saw was a transition that silently never landed.
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return
        rtlog('SETTLE-ERROR', { err: String(err).slice(0, 160) })
        throw err
      })

    this.raf = requestAnimationFrame(this.chromeTick)
  }

  /** True when the settle can run off the main thread. */
  private canComposite(): boolean {
    return typeof this.root.animate === 'function'
  }

  /** Where the composited settle has actually got to (see springProgressAt). */
  private settleProgress(): number {
    return springProgressAt(this.animations[0], this.samples, this.progress)
  }

  /** The main-thread clock for the CHROME only — no pane writes, so starving it can't stutter the slide. */
  private chromeTick = (): void => {
    if (this.state !== 'settling') return
    this.progress = this.settleProgress()
    this.publish()
    this.raf = requestAnimationFrame(this.chromeTick)
  }

  private finishSettle(target: 0 | 1): void {
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    this.progress = target
    // Take the panes back from the compositor BEFORE writing the final position, or the animation's
    // `fill: forwards` would keep overriding the inline transform we're about to set.
    this.cancelAnimations()
    this.apply()
    this.finish(target)
  }

  private cancelAnimations(): void {
    for (const animation of this.animations) animation.cancel()
    this.animations = []
    // The drag glide dies with the settle animations: every teardown/re-arm/interrupt path funnels through
    // here, so a segment can never outlive the drag that issued it (fill:forwards would pin the pane).
    this.cancelDragAnims()
  }

  /** (Re)arm the backstop: whatever phase we're in, if it hasn't reached idle in time, force a teardown so a
   *  covering snapshot can never get stuck on screen. Settle phases are ~300ms (1.2s default is well beyond);
   *  a drag re-arms this on every move, so `ms` is the max idle time a stuck-with-no-release drag survives. */
  private armWatchdog(ms = 1200): void {
    if (typeof window === 'undefined') return
    clearTimeout(this.watchdog)
    this.watchdog = window.setTimeout(() => {
      if (this.state !== 'idle') {
        this.mark('WATCHDOG-FIRE')
        this.teardown()
      }
    }, ms)
  }

  private tick = (now: number): void => {
    const dt = Math.min((now - this.lastFrame) / 1000, 1 / 30)
    this.lastFrame = now
    const displacement = this.progress - this.springTarget
    const accel = (-spring.stiffness * displacement - spring.damping * this.springV) / spring.mass
    this.springV += accel * dt
    this.progress += this.springV * dt
    // Settle on POSITION (within 1% of target ≈ a few px): the tail of a near-critically-damped spring can
    // hover with tiny residual velocity for hundreds of ms, and for a reverse (target 0) the top pane sits
    // AT the covering position that whole time — which reads as a frozen screen. Snap the moment it arrives.
    if (Math.abs(this.progress - this.springTarget) < 0.01) {
      this.progress = this.springTarget
      this.apply()
      this.raf = 0
      this.finish(this.springTarget)
      return
    }
    this.apply()
    this.raf = requestAnimationFrame(this.tick)
  }

  private apply(): void {
    this.applyPanes()
    this.publish()
  }

  /** Write the panes' transforms from `progress`. Only used when the MAIN THREAD owns them — a drag, an arm, or
   *  the final frame of a settle. While the settle is composited the animation owns them instead. */
  private applyPanes(): void {
    const leadingEdge = this.topX(this.progress)
    if (this.topEl) this.topEl.style.transform = `translate3d(${leadingEdge}px,0,0)`
    if (this.bottomEl) this.bottomEl.style.transform = `translate3d(${this.bottomX(this.progress)}px,0,0)`
    if (this.shadowEl) {
      // Parked flush against the OUTSIDE of the top pane's leading edge, so it darkens the pane beneath and
      // never bleeds over the pane that casts it.
      this.shadowEl.style.transform = `translate3d(${leadingEdge - this.stripWidth()}px,0,0)`
      this.shadowEl.style.opacity = this.stripOpacity(this.progress).toFixed(3)
    }
  }

  /** Publish `progress` to the chrome: the midpoint highlight, and the continuous position the tab underline
   *  rides. Cheap — no DOM writes here — so it's safe to run on the main thread every frame of a settle. */
  private publish(): void {
    navVisualPath.set(this.progress >= 0.5 ? this.destPath : this.originPath)
    transitionProgress.set(this.originPath, this.destPath, this.progress)
    // The physical half of that midpoint flip — the drag's counterpart to the tap tick (see haptics.ts). Only
    // for a GESTURE: a programmatic slide is a tap's own animation, and that tap already ticked on the click.
    // The seam is the pair being crossed, so a settle that carries progress on through 0.5 ticks there (which
    // is still the midpoint, and still before the route commits), and the next drag re-arms on its own pair.
    if (this.mode === 'gesture') this.midHaptic(`${this.originPath}→${this.destPath}`, this.progress)
  }

  /** Log a state-transition event and mirror the current state onto the root (read by window.__rtDump). */
  private mark(evt: string, data?: Record<string, unknown>): void {
    this.root.dataset.rtState = this.state
    this.root.dataset.rtSnaps = String(this.snapshotEls.length)
    rtlog(evt, { state: this.state, mode: this.mode, ...data })
  }

  /**
   * Safety net, run after every navigation resolves (see the outlet): when idle there must be NO snapshot
   * layers left and the live layer must be visibly clean. If a transition ever wedged and left a snapshot
   * covering the viewport, this purges it on the very next navigation — so "URL changed but the page didn't"
   * self-heals with one more click instead of staying frozen.
   */
  reconcile(): void {
    // Remove any snapshot layer that isn't part of the CURRENTLY running transition — an orphan a wedged
    // transition left behind. Safe even mid-animation: tracked panes are left untouched. The live preview
    // counts as tracked — it's React's node, and ripping it out here would leave React holding a detached ref.
    const tracked = new Set<HTMLElement>(this.snapshotEls)
    if (this.managedEl) tracked.add(this.managedEl)
    let removed = 0
    for (const el of Array.from(this.root.querySelectorAll<HTMLElement>(':scope > [data-rt-layer]'))) {
      // NEVER the live preview: React rendered that node and React alone may remove it. A link slide mounts it
      // BEFORE arming the controller, so at this moment it isn't `tracked` yet — purging it here tore React's
      // node out from under it, and React then died trying to unmount a child that had already vanished
      // (`NotFoundError: removeChild`). It is released the proper way, via `releasePreview`, on teardown.
      if (el.getAttribute('data-rt-layer') === 'preview') continue
      if (!tracked.has(el)) {
        el.remove()
        removed++
      }
    }
    if (removed > 0) this.mark('reconcile-purged-orphan', { removed })
    // Only when nothing is animating is it safe to reset the live layer + root chrome to their idle state.
    if (this.state === 'idle') {
      this.resetLive()
      for (const el of Array.from(this.root.querySelectorAll(':scope > [data-rt-shadow]'))) el.remove()
      this.shadowEl = null
      this.root.style.overflow = ''
      this.root.style.background = ''
    }
  }

  /** Strip the styles we wrote onto React's live preview pane and let React unmount it. */
  private releaseManaged(): void {
    const el = this.managedEl
    if (!el) return
    this.managedEl = null
    // Hand it back the way React rendered it. A pane that was un-inerted must not go back inert-less: React's
    // JSX carries `inert`, so it won't re-add the attribute itself if this node is ever reused.
    setFlag(el, 'inert', true)
    el.style.transform = ''
    el.style.zIndex = ''
    el.style.boxShadow = ''
    el.style.background = ''
    // Skip during dispose: the React tree is already unmounting, and a setState there would be shouting into
    // a torn-down component.
    if (!this.disposed) this.callbacks.releasePreview?.()
  }

  private resetLive(): void {
    setFlag(this.live, 'inert', false)
    this.live.style.transform = ''
    this.live.style.zIndex = ''
    this.live.style.boxShadow = ''
    this.live.style.background = ''
    this.live.style.pointerEvents = ''
  }

  /**
   * The animation has landed on an endpoint (0 = origin, 1 = destination). Bring the ROUTER to that same
   * endpoint — and only if it isn't already there. Stated that way the four cases fall out symmetrically, and
   * an in-flight transition can be grabbed and dragged to either end no matter how it started:
   *   • gesture completed        (¬committed → 1): navigate to the destination.
   *   • gesture cancelled        (¬committed → 0): nothing to undo — just drop the panes.
   *   • link slide let run       ( committed → 1): the router is already there — drop the panes.
   *   • link slide dragged back  ( committed → 0): navigate BACK to the origin.
   * Whichever pane covers the viewport at the endpoint hides the route swapping underneath, so we hold it
   * until the navigation has resolved AND painted, then tear down.
   */
  private finish(target: 0 | 1): void {
    const landedOn = target === 1 ? (this.transition?.to ?? '') : this.originPath
    if (!landedOn) {
      this.teardown()
      return
    }
    const needsNavigation = target === 1 ? !this.committed : this.committed
    this.committed = target === 1 // where the router is headed — so a re-grab mid-commit knows the truth

    // Before issuing OUR navigation, check the router still sits where this transition believes it does: a
    // commit expects it on the origin (a gesture never moves the URL), a revert expects it on the slide's
    // destination. A mismatch means a FOREIGN navigation landed mid-settle — a link clicked during the
    // spring, which gesture mode can't retarget, so the outlet dropped the visuals and the router ran it
    // alone. The user's explicit navigation wins: never commit the stale destination on top of it.
    if (needsNavigation) {
      const current = this.callbacks.getLocation()
      const expected = target === 1 ? this.originPath : (this.transition?.to ?? '')
      if (expected && !matchesFrom(current, expected)) {
        this.mark('finish→superseded', { landedOn, current })
        if (matchesFrom(current, landedOn)) {
          // The foreign navigation is heading exactly where the slide landed — nothing to commit; hold the
          // covering pane and let setLivePath release it once the live layer shows the destination.
          this.setState('finishing')
          this.awaiting = landedOn
          this.armWatchdog()
          return
        }
        // Heading somewhere else entirely — get out of its way and reveal the live layer.
        this.teardown()
        return
      }
    }

    if (needsNavigation) {
      this.setState('finishing')
      this.awaiting = landedOn
      this.armWatchdog()
      this.mark(target === 1 ? 'finish→commit' : 'finish→back', { to: landedOn })
      void this.runInternalNav(() => this.callbacks.navigate(landedOn))
      return // teardown is released by setLivePath, once the live layer actually holds `landedOn`
    }

    // Nothing to navigate here — the commit already happened (a link slide, or a gesture that committed on
    // release). The router may still be ON ITS WAY to `landedOn` (its render hasn't reached the live layer yet),
    // so hold the covering pane until it has, or teardown would reveal the page we just left. BUT if a foreign
    // navigation superseded us mid-settle (a link clicked during the spring — gesture mode can't retarget it),
    // the router is no longer bound for `landedOn`: don't wait on a live layer that will never show it — get out
    // of the way and reveal wherever the router actually went, instead of wedging until the watchdog.
    if (!matchesFrom(this.livePath, landedOn)) {
      const current = this.callbacks.getLocation()
      if (!matchesFrom(current, landedOn)) {
        this.mark('finish→superseded', { landedOn, current })
        this.teardown()
        return
      }
      this.setState('finishing')
      this.awaiting = landedOn
      this.armWatchdog()
      this.mark('finish→await-live', { landedOn, live: this.livePath })
      return
    }
    this.mark(target === 1 ? 'finish→settled' : 'finish→cancel')
    this.teardown()
  }

  private teardown(): void {
    if (typeof window !== 'undefined') clearTimeout(this.watchdog)
    this.watchdog = 0
    this.cancelAnimations() // the panes are about to be removed; a `fill: forwards` animation must not outlive them
    for (const el of this.snapshotEls) el.remove()
    this.snapshotEls = []
    this.releaseManaged()
    this.removeShadow()
    this.incomingEl = null
    this.topEl = null
    this.bottomEl = null
    this.transition = null
    this.root.style.overflow = ''
    this.root.style.background = ''
    this.resetLive()
    this.progress = 0
    this.springV = 0
    this.setState('idle')
    // Only reset the shared chrome stores when the running slide is OURS: they're module-global and outlets
    // nest, so a controller disposed mid-slide of a SIBLING surface (e.g. the admin tab pager unmounting
    // while the root surface glides) must not blank that slide's underline/active-link mid-animation.
    const running = transitionProgress.get()
    if (!running || (running.from === this.originPath && running.to === this.destPath)) {
      navVisualPath.set(null)
      transitionProgress.clear()
    }
    this.mark('teardown→idle')
  }

  dispose(): void {
    this.disposed = true
    this.cancelAnimations()
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    if (this.state !== 'idle') this.teardown()
  }
}
